import type { AdminUser, AICallLogEntry, AdminFlowModel, AdminTransferStyle, GenerationRecordEntry, OverviewStats, PaginatedResponse, WalletDetail } from './types';

export interface AdminPageContext {
  section: string;
  sectionLabel: string;
  sectionData?: Record<string, any>;
}

export interface AdminAssistantAction {
  type:
    | 'navigate'
    | 'hint'
    | 'flow-model-set-meta'
    | 'flow-model-add-preset'
    | 'flow-model-add-question'
    | 'flow-model-add-page'
    | 'flow-model-set-page';
  description: string;
  autoExecute?: boolean;
  tab?: string;
  message?: string;
  /** flow-model-set-meta */
  payload?: Record<string, string>;
  /** flow-model-add-preset */
  presetId?: string;
  /** flow-model-add-question */
  question?: {
    id: string;
    type: 'select' | 'text' | 'image' | 'boolean';
    title: string;
    required: boolean;
    promptStr?: string;
    promptTrue?: string;
    promptFalse?: string;
    options?: { value: string; label: string; prompt?: string }[];
  };
  /** flow-model-set-page */
  page?: number;
}

export interface AdminAssistantResponse {
  sessionId: string;
  text: string;
  actions: AdminAssistantAction[];
}

const BASE = '/api/admin';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers as Record<string, string> ?? {}) },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const adminApi = {
  // Phase C2：後台登入改以 auth-service BackstageAccount 帳密驗證（送 account）
  login: (account: string, password: string) =>
    request<{ username: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ account, password }) }),

  logout: () => request('/auth/logout', { method: 'POST' }),

  me: () => request<{ id: string; username: string }>('/auth/me'),

  getAiCallLogs: (params: { page?: number; accountId?: string } = {}) => {
    const q = new URLSearchParams({ limit: '20' });
    if (params.page) q.set('page', String(params.page));
    if (params.accountId) q.set('accountId', params.accountId);
    return request<PaginatedResponse<AICallLogEntry>>(`/ai-call-logs?${q}`);
  },

  getGenerationRecords: (params: { page?: number; accountId?: string; type?: string; startDate?: string; endDate?: string } = {}) => {
    const q = new URLSearchParams({ limit: '20' });
    if (params.page) q.set('page', String(params.page));
    if (params.accountId) q.set('accountId', params.accountId);
    if (params.type) q.set('type', params.type);
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    return request<PaginatedResponse<GenerationRecordEntry>>(`/generation-records?${q}`);
  },

  getAiCallLogsByRecords: (recordIds: string[]) => {
    const q = new URLSearchParams({ limit: '500' });
    q.set('recordIds', recordIds.join(','));
    return request<PaginatedResponse<AICallLogEntry>>(`/ai-call-logs?${q}`);
  },

  getOverviewStats: () => request<OverviewStats>('/stats/overview'),

  getAccounts: () => request<AdminUser[]>('/accounts'),

  getAccountOwnerMap: () => request<Record<string, string>>('/accounts/owner-map'),

  updateOwnerName: (id: string, ownerName: string) =>
    request(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify({ ownerName }) }),

  updateExpiresAt: (id: string, expiresAt: string | null) =>
    request(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify({ expiresAt }) }),

  updateFreeQuota: (id: string, freeQuota: number | null) =>
    request(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify({ freeQuota }) }),

  updateJoleehiVillageId: (id: string, joleehiVillageId: string | null) =>
    request(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify({ joleehiVillageId }) }),

  getFlowModels: () => request<AdminFlowModel[]>('/flow-models'),

  createFlowModel: (data: AdminFlowModel) =>
    request<AdminFlowModel>('/flow-models', { method: 'POST', body: JSON.stringify(data) }),

  updateFlowModel: (id: string, data: AdminFlowModel) =>
    request<AdminFlowModel>(`/flow-models/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteFlowModel: (id: string) =>
    request(`/flow-models/${id}`, { method: 'DELETE' }),

  reorderFlowModels: (ids: string[]) =>
    request('/flow-models/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),

  getTransferStyles: () => request<AdminTransferStyle[]>('/transfer-styles'),

  createTransferStyle: (data: AdminTransferStyle) =>
    request<AdminTransferStyle>('/transfer-styles', { method: 'POST', body: JSON.stringify(data) }),

  updateTransferStyle: (id: string, data: AdminTransferStyle) =>
    request<AdminTransferStyle>(`/transfer-styles/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTransferStyle: (id: string) =>
    request(`/transfer-styles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  getToolkitCategories: () =>
    request<{ name: string; order: number }[]>('/toolkit-categories'),

  getSupplementTools: () =>
    request<{ id: string; label: string }[]>('/supplement-tools'),

  createToolkitCategory: (name: string) =>
    request<{ name: string; order: number }>('/toolkit-categories', { method: 'POST', body: JSON.stringify({ name }) }),

  deleteToolkitCategory: (name: string) =>
    request(`/toolkit-categories/${encodeURIComponent(name)}`, { method: 'DELETE' }),

  uploadToolkitImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file, file.name);
    const res = await fetch(`${BASE}/upload/toolkit-image`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Upload failed');
    }
    return res.json();
  },

  testTransferStyle: async (args: {
    image: File; prompt: string; name?: string; extraRequirements?: string;
  }): Promise<{ imageUrl: string }> => {
    const formData = new FormData();
    formData.append('image', args.image, args.image.name);
    formData.append('prompt', args.prompt);
    if (args.name) formData.append('name', args.name);
    if (args.extraRequirements) formData.append('extraRequirements', args.extraRequirements);
    const res = await fetch(`${BASE}/transfer-styles/test-generate`, {
      method: 'POST', credentials: 'include', body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || '測試生成失敗');
    }
    return res.json();
  },

  suggestTransferStylePrompt: async (args: {
    source: File; target: File; notes?: string;
  }): Promise<{ prompt: string }> => {
    const formData = new FormData();
    formData.append('source', args.source, args.source.name);
    formData.append('target', args.target, args.target.name);
    if (args.notes) formData.append('notes', args.notes);
    const res = await fetch(`${BASE}/transfer-styles/suggest-prompt`, {
      method: 'POST', credentials: 'include', body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || '產生 prompt 失敗');
    }
    return res.json();
  },

  getUserWallets: (accountId: string) => {
    const q = new URLSearchParams({ accountId });
    return request<WalletDetail[]>(`/wallets?${q}`);
  },

  grantUserPoints: (accountId: string, points: number, reason?: string) =>
    request<{ success: boolean; currentPoints: number }>(
      `/wallets/${encodeURIComponent(accountId)}/grant`,
      { method: 'POST', body: JSON.stringify({ points, reason }) }
    ),

  sendAdminAssistantMessage: (params: { sessionId?: string; message: string; pageContext: AdminPageContext }) =>
    request<AdminAssistantResponse>('/assistant/chat', { method: 'POST', body: JSON.stringify(params) }),
};
